import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { format, subHours } from "date-fns";
import { ArrowLeft, Clock, User, Music, Users, AlertCircle, CheckCircle, XCircle, Shuffle, MessageSquare, Eye, ArrowRight, UserPlus, MoveRight, Mic2, Guitar, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";
import { useWebSocket } from "@/hooks/use-websocket";
import { useBrowserDetection } from "@/hooks/use-browser-detection";
import { PageBackground } from "@/components/ui/page-background";
import { Badge } from "@/components/ui/badge";
import ReactMarkdown from 'react-markdown';

interface FeedEntry {
  id: string;
  type: 'feed_entry' | 'message';
  eventId: number;
  action: string;
  details: any;
  timestamp: string;
  performerName?: string;
  participantName?: string;
  userName?: string;
}

export function EventFeed() {
  const { eventId } = useParams();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { isChrome } = useBrowserDetection();
  const [entries, setEntries] = useState<FeedEntry[]>([]);
  const [showMessages, setShowMessages] = useState(true);

  // Fetch event details
  const { data: event } = useQuery<any>({
    queryKey: [`/api/events/${eventId}`],
    enabled: !!eventId && !!user,
  });

  // Fetch feed entries
  const { data: feedData, isLoading, refetch } = useQuery({
    queryKey: [`/api/events/${eventId}/feed`],
    enabled: !!eventId && !!user,
  });

  useEffect(() => {
    if (feedData) {
      // Adjust timestamps by subtracting 1 hour for timezone correction
      const adjustedEntries = (feedData as FeedEntry[]).map(entry => ({
        ...entry,
        timestamp: new Date(subHours(new Date(entry.timestamp), 1)).toISOString()
      }));
      setEntries(adjustedEntries);
    }
  }, [feedData]);

  // Subscribe to WebSocket updates - the hook handles invalidation automatically
  useWebSocket();

  const getActionIcon = (entry: FeedEntry) => {
    if (entry.type === 'message') {
      return <MessageSquare className="h-5 w-5 text-white" />;
    }
    
    switch (entry.action) {
      case 'participant_registered':
        return <Users className="h-5 w-5 text-white" />;
      case 'performer_selected':
      case 'performer_swapped':
        return <CheckCircle className="h-5 w-5 text-white" />;
      case 'performer_marked_consideration':
        return <Eye className="h-5 w-5 text-white" />;
      case 'performer_removed_from_consideration':
        return <Eye className="h-5 w-5 text-white" />;
      case 'performer_on_stage':
        return <Music className="h-5 w-5 text-white" />;
      case 'performer_marked_for_removal':
        return <AlertCircle className="h-5 w-5 text-white" />;
      case 'performer_finished':
        return <CheckCircle className="h-5 w-5 text-white" />;
      case 'performer_deselected':
        return <XCircle className="h-5 w-5 text-white" />;
      case 'instruments_changed':
        return <Shuffle className="h-5 w-5 text-white" />;
      default:
        return <Clock className="h-5 w-5 text-white" />;
    }
  };


  const groupedEntries = useMemo(() => {
    const groups: { [key: string]: FeedEntry[] } = {};
    
    // Filter out messages if showMessages is false
    const filteredEntries = showMessages 
      ? entries 
      : entries.filter(entry => entry.type !== 'message');
    
    filteredEntries.forEach(entry => {
      const date = format(new Date(entry.timestamp), 'yyyy-MM-dd');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(entry);
    });

    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, entries]) => ({
        date,
        entries: entries.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        )
      }));
  }, [entries, showMessages]);

  if (isLoading) {
    return (
      <PageBackground>
        <div className="container mx-auto p-6">
          <div className="space-y-4">
            <Skeleton className="h-12 w-48" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <div className="container mx-auto p-4 sm:p-6">
        {/* Mobile Layout */}
        <div className="mb-6 flex flex-col gap-4 lg:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setLocation(`/event/${eventId}`)}
                className="bg-white/20 backdrop-blur-xl border border-white/20 hover:bg-white/30"
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.2)',
                }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-2xl font-bold text-gray-800 drop-shadow-sm">
                Event Feed
              </h1>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMessages(!showMessages)}
              className={`flex items-center gap-1.5 transition-all backdrop-blur-md ${
                showMessages 
                  ? 'bg-blue-50/30 border-blue-200/50 hover:bg-blue-100/40' 
                  : 'bg-white/30 border-white/30 hover:bg-white/40'
              }`}
            >
              <MessageSquare className="h-4 w-4" />
              <span className="text-xs sm:text-sm">
                {showMessages ? 'Hide' : 'Show'}
                <span className="hidden sm:inline"> Messages</span>
              </span>
            </Button>
          </div>
          {event && (
            <div className="text-sm text-gray-600 text-center px-2">
              <div className="font-medium">{event.name}</div>
              <div className="text-xs">{format(new Date(event.date), 'PPP')}</div>
            </div>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="mb-6 hidden lg:flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLocation(`/event/${eventId}`)}
              className="bg-white/20 backdrop-blur-xl border border-white/20 hover:bg-white/30"
              style={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold text-gray-800 drop-shadow-sm">
              Event Feed
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMessages(!showMessages)}
              className={`flex items-center gap-2 transition-all backdrop-blur-md ${
                showMessages 
                  ? 'bg-blue-50/30 border-blue-200/50 hover:bg-blue-100/40' 
                  : 'bg-white/30 border-white/30 hover:bg-white/40'
              }`}
            >
              <MessageSquare className="h-4 w-4" />
              <span className="text-sm">
                {showMessages ? 'Hide' : 'Show'} Messages
              </span>
            </Button>
            {event && (
              <div className="text-sm text-gray-600">
                {event.name} - {format(new Date(event.date), 'PPP')}
              </div>
            )}
          </div>
        </div>

        {groupedEntries.length === 0 ? (
          <Card 
            className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
          >
            <CardContent className="p-8 text-center">
              <Clock className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600">No activity recorded yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-1">
            {groupedEntries.map(({ date, entries }) => (
              <div key={date}>
                <div className="sticky top-0 z-10 bg-gradient-to-r from-white/40 to-gray-50/40 backdrop-blur-xl px-4 py-2.5 mb-3 rounded-xl border border-white/30 shadow-sm">
                  <h2 className="text-sm font-bold text-gray-700 drop-shadow-sm">
                    {format(new Date(date), 'EEEE, MMMM d, yyyy')}
                  </h2>
                </div>
                <div className="space-y-2">
                  {entries.map((entry) => {
                    const isMessage = entry.type === 'message';
                    const details = entry.details || {};
                    const name = entry.performerName || entry.participantName || "Unknown";
                    
                    // Message Component
                    if (isMessage) {
                      return (
                        <div key={entry.id} className="group relative">
                          <div className="flex gap-3 p-4 rounded-xl bg-gradient-to-r from-blue-50/30 to-indigo-50/30 backdrop-blur-md border border-blue-100/30 hover:shadow-lg transition-all duration-300">
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-md">
                                <MessageSquare className="h-5 w-5 text-white" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-2 mb-1">
                                <span className="font-semibold text-gray-900">
                                  {entry.userName || 'Anonymous'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {format(new Date(entry.timestamp), 'h:mm a')}
                                </span>
                              </div>
                              <div className="prose prose-sm max-w-none text-gray-700 break-words">
                                <ReactMarkdown>
                                  {details.content}
                                </ReactMarkdown>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Status Change Component  
                    if (entry.action.includes('selected') || entry.action.includes('deselected') || 
                        entry.action.includes('consideration') || entry.action.includes('on_stage') || 
                        entry.action.includes('finished') || entry.action.includes('removal')) {
                      
                      const statusStyles: Record<string, { gradient: string; icon: string; label: string; badgeClass: string }> = {
                        'participant': { 
                          gradient: 'from-gray-400 to-gray-600', 
                          icon: 'text-gray-600', 
                          label: 'Waiting',
                          badgeClass: 'bg-gray-100 text-gray-700 border-gray-200'
                        },
                        'consideration': { 
                          gradient: 'from-yellow-400 to-amber-600', 
                          icon: 'text-yellow-600', 
                          label: 'Consideration',
                          badgeClass: 'bg-yellow-100 text-yellow-800 border-yellow-200'
                        },
                        'selected': { 
                          gradient: 'from-blue-400 to-blue-600', 
                          icon: 'text-blue-600', 
                          label: 'Selected',
                          badgeClass: 'bg-blue-100 text-blue-800 border-blue-200'
                        },
                        'on_stage': { 
                          gradient: 'from-green-400 to-green-600', 
                          icon: 'text-green-600', 
                          label: 'On Stage',
                          badgeClass: 'bg-green-100 text-green-800 border-green-200'
                        },
                        'pending_removal': { 
                          gradient: 'from-red-400 to-red-600', 
                          icon: 'text-red-600', 
                          label: 'Sub Out',
                          badgeClass: 'bg-red-100 text-red-800 border-red-200'
                        }
                      };
                      
                      const fromStatus = statusStyles[details.previousStatus as string] || statusStyles['participant'];
                      const toStatus = statusStyles[details.newStatus as string] || statusStyles['participant'];
                      
                      return (
                        <div key={entry.id} className="group relative">
                          <div className="flex gap-3 p-4 rounded-xl bg-white/40 backdrop-blur-md border border-white/30 hover:shadow-lg transition-all duration-300">
                            <div className="flex-shrink-0">
                              <div className={`h-10 w-10 rounded-full bg-gradient-to-br ${toStatus.gradient} flex items-center justify-center shadow-md`}>
                                {getActionIcon(entry)}
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="mb-1">
                                    <span className="font-semibold text-gray-900">{name}</span>
                                  </div>
                                  
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <Badge variant="outline" className={`${fromStatus.badgeClass} border px-2 py-0 text-xs`}>
                                      {fromStatus.label}
                                    </Badge>
                                    <MoveRight className="h-3 w-3 text-gray-400" />
                                    <Badge variant="outline" className={`${toStatus.badgeClass} border px-2 py-0 text-xs font-semibold`}>
                                      {toStatus.label}
                                    </Badge>
                                  </div>
                                  
                                  {entry.action === 'performer_marked_for_removal' && (
                                    <div className="text-sm text-gray-700 font-medium">
                                      Performer marked for removal
                                    </div>
                                  )}
                                  
                                  {details.instruments && details.instruments.length > 0 && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                      <Guitar className="h-3.5 w-3.5 text-gray-400" />
                                      <span className="text-sm text-gray-600">{details.instruments.join(', ')}</span>
                                    </div>
                                  )}
                                  
                                  {details.replacedPerformerName && (
                                    <p className="text-sm text-gray-600 mt-1">
                                      Replacing <span className="font-medium">{details.replacedPerformerName}</span>
                                    </p>
                                  )}
                                  
                                  {details.replacementName && (
                                    <p className="text-sm text-gray-600 mt-1">
                                      <span className="font-medium">{details.replacementName}</span> will replace
                                    </p>
                                  )}
                                  
                                  {details.timeOnStage && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                      <Clock className="h-3.5 w-3.5 text-gray-400" />
                                      <span className="text-sm text-gray-600">Performed for {details.timeOnStage} minutes</span>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="text-right ml-4">
                                  <div className="text-xs text-gray-500">{format(new Date(entry.timestamp), 'h:mm a')}</div>
                                  {entry.userName && (
                                    <div className="text-xs text-gray-400 mt-0.5">by {entry.userName}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Registration Component
                    if (entry.action === 'participant_registered') {
                      return (
                        <div key={entry.id} className="group relative">
                          <div className="flex gap-3 p-4 rounded-xl bg-gradient-to-r from-green-50/30 to-emerald-50/30 backdrop-blur-md border border-green-100/30 hover:shadow-lg transition-all duration-300">
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-md">
                                <UserPlus className="h-5 w-5 text-white" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-gray-900">{name}</span>
                                    <span className="text-sm text-gray-600">registered</span>
                                    {details.priority && (
                                      <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300 px-2 py-0 text-xs font-semibold shadow-sm">
                                        Priority
                                      </Badge>
                                    )}
                                    {details.tier && (
                                      <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300 px-2 py-0 text-xs font-semibold shadow-sm">
                                        {details.tier}
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  {details.instruments && details.instruments.length > 0 && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                      <Guitar className="h-3.5 w-3.5 text-gray-400" />
                                      <span className="text-sm text-gray-700 font-medium">
                                        {details.instruments.join(', ')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="text-right ml-4">
                                  <div className="text-xs text-gray-500">{format(new Date(entry.timestamp), 'h:mm a')}</div>
                                  {entry.userName && (
                                    <div className="text-xs text-gray-400 mt-0.5">by {entry.userName}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Instrument Change Component
                    if (entry.action === 'instruments_changed') {
                      return (
                        <div key={entry.id} className="group relative">
                          <div className="flex gap-3 p-4 rounded-xl bg-gradient-to-r from-purple-50/30 to-indigo-50/30 backdrop-blur-md border border-purple-100/30 hover:shadow-lg transition-all duration-300">
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-indigo-600 flex items-center justify-center shadow-md">
                                <Shuffle className="h-5 w-5 text-white" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-semibold text-gray-900">{name}</span>
                                    <span className="text-sm text-gray-600">changed instruments</span>
                                  </div>
                                  
                                  <div className="flex items-center gap-2 bg-white/30 backdrop-blur-md rounded-lg px-3 py-1.5 border border-purple-100/20">
                                    <div className="flex items-center gap-1.5">
                                      <Guitar className="h-3.5 w-3.5 text-gray-500" />
                                      <span className="text-sm text-gray-600">
                                        {details.previousInstruments?.join(', ') || 'none'}
                                      </span>
                                    </div>
                                    <MoveRight className="h-4 w-4 text-purple-500" />
                                    <div className="flex items-center gap-1.5">
                                      <Guitar className="h-3.5 w-3.5 text-purple-600" />
                                      <span className="text-sm font-semibold text-purple-800">
                                        {details.newInstruments?.join(', ') || 'none'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="text-right ml-4">
                                  <div className="text-xs text-gray-500">{format(new Date(entry.timestamp), 'h:mm a')}</div>
                                  {entry.userName && (
                                    <div className="text-xs text-gray-400 mt-0.5">by {entry.userName}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Default fallback
                    return (
                      <div key={entry.id} className="group relative">
                        <div className="flex gap-3 p-4 rounded-xl bg-white/40 backdrop-blur-md border border-white/30 hover:shadow-lg transition-all duration-300">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center shadow-md">
                              {getActionIcon(entry)}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900">{name}</span>
                                  <span className="text-sm text-gray-600">{entry.action.replace(/_/g, ' ')}</span>
                                </div>
                              </div>
                              
                              <div className="text-right ml-4">
                                <div className="text-xs text-gray-500">{format(new Date(entry.timestamp), 'h:mm a')}</div>
                                {entry.userName && (
                                  <div className="text-xs text-gray-400 mt-0.5">by {entry.userName}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageBackground>
  );
}